import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Lang, t } from "../i18n";

type Stat = {
  key: string;
  targetValue: number;
  suffixScale: string;
};

const stats: Stat[] = [
  { key: "1", targetValue: 38, suffixScale: "text-[0.6em]" },
  { key: "2", targetValue: 24, suffixScale: "text-[0.5em]" },
  { key: "3", targetValue: 70, suffixScale: "text-[0.6em]" },
];

const easeOutQuart = (progress: number) => 1 - Math.pow(1 - progress, 4);

function useAnimatedNumber(target: number, active: boolean, delayMs: number) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }

    let animationFrame = 0;
    let timeout = 0;

    timeout = window.setTimeout(() => {
      const startedAt = performance.now();
      const duration = 880;

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        setValue(Math.round(target * easeOutQuart(progress)));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        }
      };

      animationFrame = requestAnimationFrame(tick);
    }, delayMs);

    return () => {
      window.clearTimeout(timeout);
      cancelAnimationFrame(animationFrame);
    };
  }, [active, delayMs, target]);

  return value;
}

function StatCard({
  lang,
  stat,
  inView,
  index,
}: {
  lang: Lang;
  stat: Stat;
  inView: boolean;
  index: number;
}) {
  const numberDelay = 800 + index * 90;
  const value = useAnimatedNumber(stat.targetValue, inView, numberDelay);

  return (
    <motion.article
      role="figure"
      aria-label={`${t(lang, `pain.stat.${stat.key}.label`)}: ${t(lang, `pain.stat.${stat.key}.num`)}${t(lang, `pain.stat.${stat.key}.suffix`)}`}
      className="relative mx-auto w-full max-w-[280px] overflow-hidden rounded-[22px] border border-[#e7dce7] bg-white p-7 text-center shadow-[0_24px_70px_rgba(46,22,152,.08)] md:max-w-none lg:p-8"
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
      transition={{ duration: 0.5, delay: 0.6 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.4, delay: 1.2 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background:
            "radial-gradient(circle at 50% 34%, rgba(217,35,199,0.14) 0%, rgba(82,35,177,0.08) 32%, transparent 64%)",
        }}
      />

      <div className="relative">
        <div
          aria-live="polite"
          className="mb-5 font-mono text-[64px] font-black leading-none tracking-[-0.02em] text-transparent tabular-nums sm:text-[72px] lg:text-[82px]"
        >
          <span className="bg-[linear-gradient(135deg,#5223b1_0%,#7b2bc4_48%,#d923c7_100%)] bg-clip-text">
            {value}
          </span>
          <motion.span
            className={[
              "ml-1 bg-[linear-gradient(135deg,#5223b1_0%,#d923c7_100%)] bg-clip-text align-baseline font-black text-transparent",
              stat.suffixScale,
            ].join(" ")}
            initial={{ opacity: 0, y: 6 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
            transition={{ duration: 0.22, delay: 0.92 + index * 0.08, ease: "easeOut" }}
          >
            {t(lang, `pain.stat.${stat.key}.suffix`)}
          </motion.span>
        </div>
        <div className="mb-4 text-sm font-bold leading-snug text-[#3a383f]">
          {t(lang, `pain.stat.${stat.key}.label`)}
        </div>
        <div className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#9a8da0]">
          {t(lang, `pain.stat.${stat.key}.source`)}
        </div>
      </div>
    </motion.article>
  );
}

export function PainPoints({ lang }: { lang: Lang }) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  return (
    <section
      ref={ref}
      id="pain"
      aria-label={t(lang, "pain.aria")}
      className="relative overflow-hidden bg-[#f8f9fa] px-5 pb-10 pt-14 sm:px-10 lg:pb-12 lg:pt-16"
    >
      <div className="relative mx-auto flex w-full max-w-[1200px] flex-col items-center text-center">
        <motion.div
          className="inline-flex w-fit rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#5223b1]"
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
        >
          {t(lang, "pain.badge")}
        </motion.div>
        <motion.h2
          className="mt-5 max-w-[980px] text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#100f12] sm:text-[48px] md:text-[58px]"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {t(lang, "pain.title")
            .split("\n")
            .map((line, index) => (
              <span
                key={`${line}-${index}`}
                className={
                  index === 1
                    ? "block bg-[linear-gradient(135deg,#5223b1_0%,#7b2bc4_48%,#d923c7_100%)] bg-clip-text text-transparent"
                    : "block"
                }
              >
                {line}
              </span>
            ))}
        </motion.h2>

        <motion.p
          className="mx-auto mt-6 max-w-[860px] text-base leading-relaxed text-[#3a383f] sm:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          transition={{ duration: 0.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {t(lang, "pain.subtitle")}
        </motion.p>

        <div className="mt-16 grid w-full grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {stats.map((stat, index) => (
            <StatCard key={stat.key} lang={lang} stat={stat} inView={inView} index={index} />
          ))}
        </div>

        <motion.div
          className="mt-14 flex flex-col items-center gap-3 text-sm font-bold text-[#775f78]"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 1.4, ease: "easeOut" }}
        >
          <span className="rounded-full border border-[#e7dce7] bg-white/80 px-4 py-2 shadow-[0_12px_34px_rgba(46,22,152,.07)]">
            {t(lang, "pain.hint")}
          </span>
          <motion.span
            aria-hidden="true"
            className="text-xl leading-none text-[#5223b1]"
            animate={{ y: [0, 6, 0], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            ↓
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
